import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, ThumbsUp, ThumbsDown } from "lucide-react";
import { http, errMsg } from "../../lib/api";
import { fetchIdeas } from "../../lib/ideas";

const field = "w-full rounded-xl border border-cyan-400/20 bg-[#050911] px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/70";

export const AdminIdeas = () => {
  const [ideas, setIdeas] = useState([]);
  const [f, setF] = useState(null);

  const load = useCallback(async () => setIdeas(await fetchIdeas()), []);
  useEffect(() => { load(); }, [load]);

  const save = async (e) => {
    e.preventDefault();
    try { await http.post("/ideas", f); toast.success("Idea posted for voting"); setF(null); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  const remove = async (it) => {
    if (!window.confirm(`Delete idea "${it.title}"?`)) return;
    try { await http.delete(`/ideas/${it.id}`); toast.success("Idea deleted"); load(); } catch (err) { toast.error(errMsg(err)); }
  };

  return (
    <section className="mt-14" data-testid="admin-ideas">
      <div className="mb-6 flex items-center justify-between">
        <h2 className="font-display text-4xl font-black uppercase">Ideas</h2>
        <button onClick={() => setF({ title: "", description: "" })} data-testid="admin-create-idea-button"
          className="flex items-center gap-2 rounded-full bg-[#00E5FF] px-4 py-2 font-mono text-xs font-bold uppercase tracking-[0.12em] text-[#050911] hover:bg-white transition-colors">
          <Plus className="h-4 w-4" /> New idea
        </button>
      </div>
      {f && (
        <form onSubmit={save} data-testid="admin-idea-form" className="mb-6 space-y-3 rounded-2xl border border-cyan-400/30 bg-[#0A101D] p-5">
          <input required minLength={3} maxLength={120} placeholder="Idea title" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={field} data-testid="admin-idea-title-input" />
          <textarea rows={3} maxLength={1000} placeholder="Description (optional)" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className={field} data-testid="admin-idea-description-input" />
          <div className="flex justify-end gap-2">
            <button type="button" onClick={() => setF(null)} className="rounded-full px-4 py-2 font-mono text-xs uppercase text-slate-300 hover:text-white" data-testid="admin-idea-cancel-button">Cancel</button>
            <button type="submit" className="rounded-full bg-[#00E5FF] px-5 py-2 font-mono text-xs font-bold uppercase text-[#050911] hover:bg-white" data-testid="admin-idea-save-button">Post for voting</button>
          </div>
        </form>
      )}
      <ul className="space-y-3">
        {ideas.map((it, i) => (
          <li key={it.id} data-testid={`admin-idea-${i}`} className="flex items-start justify-between gap-4 rounded-2xl border border-cyan-400/12 bg-[#0F1829] p-5">
            <div className="min-w-0">
              <p className={`font-mono text-[11px] uppercase tracking-[0.2em] ${it.source === "team" ? "text-[#00E5FF]" : "text-[#F59E0B]"}`}>{it.source === "team" ? "Team" : `Community · ${it.author}`}</p>
              <p className="mt-1 font-display text-xl font-bold uppercase break-words">{it.title}</p>
              <p className="mt-2 flex items-center gap-4 font-mono text-xs text-slate-300">
                <span className="flex items-center gap-1"><ThumbsUp className="h-3.5 w-3.5" /> {it.up}</span>
                <span className="flex items-center gap-1"><ThumbsDown className="h-3.5 w-3.5" /> {it.down}</span>
              </p>
            </div>
            <button onClick={() => remove(it)} data-testid={`admin-delete-idea-${i}`} className="shrink-0 rounded-full border border-red-500/30 p-2 text-red-400 hover:border-red-400"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
        {ideas.length === 0 && <p className="text-slate-400">No ideas yet.</p>}
      </ul>
    </section>
  );
};
